/**
 * @file feed-notification.ts
 * @description Тексты уведомления клиенту в ленте (phase19.31, uiux-design.md
 * §4.1 «Тексты»; контракт §9: `channel`, `status`, `reason`).
 *
 * Реестрами, а не ветками: канал → название, исход → фраза. Коды каналов и
 * слово «mock» на экран не попадают — вместо них «Почта», «в тестовом
 * режиме». Каналы с одинаковым исходом сливаются: «SMS и почта — не ушло:
 * канал в тестовом режиме».
 */

import type { OrderFeedChannelDto } from "../../lib/api-orders";
import { NOTIFICATION_KIND_LABELS } from "../../lib/api-notification-templates";

/** Название канала: с заглавной — в начале строки, строчное — в перечислении. */
const CHANNEL_NAME: Record<string, { first: string; inner: string }> = {
  IN_APP: { first: "В кабинете", inner: "в кабинете" },
  EMAIL: { first: "Почта", inner: "почта" },
  SMS: { first: "SMS", inner: "SMS" },
  TELEGRAM: { first: "Telegram", inner: "Telegram" },
  PUSH: { first: "Push-уведомление", inner: "push-уведомление" },
};
const UNKNOWN_CHANNEL = { first: "Другой канал", inner: "другой канал" };

/** Причина «не отправлялось» (`skipped`) — человеческой фразой. */
const SKIPPED_REASON: Record<string, string> = {
  channel_unconfigured: "канал не подключён, клиенту не ушло",
  no_email: "не ушло: у клиента нет почты",
  no_phone: "не ушло: у клиента нет телефона",
  channel_unavailable: "не ушло: канал недоступен",
  template_missing: "не ушло: нет шаблона сообщения",
};

/** Исход одного канала фразой (без названия канала). */
export function channelOutcomeText(channel: OrderFeedChannelDto): string {
  switch (channel.status) {
    case "sent":
      // В кабинете сообщение уже лежит у клиента — «доставлено»; письмо и SMS
      // мы только отправили.
      return channel.channel === "IN_APP" ? "доставлено" : "отправлено";
    case "mock":
      return "не ушло: канал в тестовом режиме";
    case "failed":
      return channel.reason === "queued_retry" ? "повторим отправку: канал временно недоступен" : "не ушло: сбой отправки";
    case "skipped":
      return (channel.reason ? SKIPPED_REASON[channel.reason] : undefined) ?? "не отправлялось";
  }
}

/** Порядок исходов в строке: сначала что дошло, затем повтор, затем «не ушло». */
const STATUS_RANK: Record<OrderFeedChannelDto["status"], number> = { sent: 0, failed: 1, skipped: 2, mock: 3 };

/**
 * Вторая линия уведомления: «В кабинете — доставлено · SMS и почта — не ушло:
 * канал в тестовом режиме». Дошедшие каналы — первыми; внутри исхода порядок
 * сервера.
 */
export function notificationChannelsLine(channels: readonly OrderFeedChannelDto[]): string | null {
  const byOutcome = new Map<string, string[]>();
  const ordered = [...channels].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
  for (const channel of ordered) {
    const outcome = channelOutcomeText(channel);
    const names = byOutcome.get(outcome) ?? [];
    names.push(channel.channel);
    byOutcome.set(outcome, names);
  }
  const parts = [...byOutcome.entries()].map(([outcome, codes]) => {
    const names = codes.map((code, index) => {
      const name = CHANNEL_NAME[code] ?? UNKNOWN_CHANNEL;
      return index === 0 ? name.first : name.inner;
    });
    return `${names.join(" и ")} — ${outcome}`;
  });
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Название шаблона «Заказ оплачен»; неизвестный вид — null. */
export function notificationTitle(templateKind: string): string | null {
  const labels: Partial<Record<string, string>> = NOTIFICATION_KIND_LABELS;
  return labels[templateKind] ?? null;
}

/** Дошло ли уведомление клиенту хоть по одному каналу. */
export function notificationReached(channels: readonly OrderFeedChannelDto[]): boolean {
  return channels.some((c) => c.status === "sent");
}

/** Сбой отправки (не тестовый режим и не плановый повтор) — красный тон. */
export function notificationFailed(channels: readonly OrderFeedChannelDto[]): boolean {
  return channels.some((c) => c.status === "failed" && c.reason !== "queued_retry");
}
